<template>
  <Content>
    <div class="container-fluid py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 class="page-title mb-1">{{ currentLang.title }}</h3>
          <p class="page-subtitle mb-0">
            {{ currentLang.invoiceNumber }}: {{ invoice.invoice_number }}
          </p>
        </div>

        <button @click="$router.back()" class="btn btn-light back-btn">
          {{ currentLang.back }}
        </button>
      </div>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
      </div>

      <div v-else>
        <div class="row g-4 mb-4">
          <div class="col-12 col-lg-4">
            <div class="info-card">
              <h6 class="card-title">{{ currentLang.userInfo }}</h6>

              <p>
                <span>{{ currentLang.fullName }}</span>
                <strong>
                  {{ invoice.user?.first_name }} {{ invoice.user?.last_name }}
                </strong>
              </p>

              <p>
                <span>{{ currentLang.mobile }}</span>
                <strong>{{ invoice.user?.mobile }}</strong>
              </p>

              <p>
                <span>{{ currentLang.school }}</span>
                <strong>{{ invoice.user?.school_name || "-" }}</strong>
              </p>
            </div>
          </div>

          <div class="col-12 col-lg-4">
            <div class="info-card">
              <h6 class="card-title">{{ currentLang.invoiceInfo }}</h6>

              <p>
                <span>{{ currentLang.orderId }}</span>
                <strong>#{{ invoice.order_id }}</strong>
              </p>

              <p>
                <span>{{ currentLang.shippingMethod }}</span>
                <strong>
                  {{ lang.value === "fa"
                    ? invoice.shipping_method?.name_fa
                    : invoice.shipping_method?.name_en }}
                </strong>
              </p>

              <p>
                <span>{{ currentLang.paymentMethod }}</span>
                <strong>
                  {{ lang.value === "fa"
                    ? invoice.payment_method?.name_fa
                    : invoice.payment_method?.name_en }}
                </strong>
              </p>
            </div>
          </div>

         <div class="col-12 col-lg-4">
  <div class="info-card">
    <h6 class="card-title">{{ currentLang.addressInfo }}</h6>

    <p>
      <span>{{ currentLang.receiverPhone }}</span>
      <strong>{{ invoice.address?.phone }}</strong>
    </p>

    <p>
      <span>{{ currentLang.city }}</span>
      <strong>
        {{ invoice.address?.province?.name }} /
        {{ invoice.address?.city?.name }}
      </strong>
    </p>

    <!-- نمایش آدرس اصلی -->
    <p>
      <span>{{ currentLang.address }}</span>
      <strong style="word-break: break-all;">{{ invoice.address?.address }}</strong>
    </p>

    <!-- نمایش جزئیات آدرس -->
    <p>
      <span>{{ currentLang.addressDetails }}</span>
      <strong style="word-break: break-all;">{{ invoice.address?.details || "-" }}</strong>
    </p>

    <p>
      <span>{{ currentLang.postalCode }}</span>
      <strong>{{ invoice.address?.postal_code }}</strong>
    </p>
  </div>
</div>

        </div>

        <div class="table-card mb-4">
          <h6 class="card-title mb-3">{{ currentLang.items }}</h6>

          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{{ currentLang.product }}</th>
                  <th>{{ currentLang.quantity }}</th>
                  <th>{{ currentLang.price }}</th>
                  <th>{{ currentLang.discount }}</th>
                  <th>{{ currentLang.tax }}</th>
                  <th>{{ currentLang.total }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(item, index) in invoice.items" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td>
                    {{ lang.value === "fa"
                      ? item.product?.name_fa
                      : item.product?.name_en }}
                  </td>
                  <td>{{ item.quantity }}</td>
                  <td>{{ formatPrice(item.price) }}</td>
                  <td>{{ formatPrice(item.total_discount) }}</td>
                  <td>{{ formatPrice(item.tax) }}</td>
                  <td class="fw-bold">{{ formatPrice(item.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="row justify-content-end">
          <div class="col-12 col-lg-5">
            <div class="summary-card">
              <div>
                <span>{{ currentLang.subtotal }}</span>
                <strong>{{ formatPrice(invoice.subtotal) }}</strong>
              </div>

              <div>
                <span>{{ currentLang.totalDiscount }}</span>
                <strong class="text-danger">
                  {{ formatPrice(invoice.total_discount) }}
                </strong>
              </div>

              <div>
                <span>{{ currentLang.shippingCost }}</span>
                <strong>{{ formatPrice(invoice.shipping_cost) }}</strong>
              </div>

              <div>
                <span>{{ currentLang.tax }}</span>
                <strong>{{ formatPrice(invoice.tax) }}</strong>
              </div>

              <div class="total-row">
                <span>{{ currentLang.totalPrice }}</span>
                <strong>{{ formatPrice(invoice.total_price) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Content>
</template>
<script setup>
import Content from "@/components/AdminContent.vue";
import api from "@/plugins/axios";
import { lang } from "@/plugins/lang";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const loading = ref(false);

const invoice = ref({
  user: {},
  shipping_method: {},
  payment_method: {},
  address: {
    province: {},
    city: {},
    country: {},
  },
  items: [],
});

import enText from "@/locales/en/invoicePrint";
import faText from "@/locales/fa/invoicePrint";

const translations = {
  en: enText,
  fa: faText,
};

const currentLang = computed(() => translations[lang.value]);

const formatPrice = (price) => {
  if (!price) return "0";

  return Number(price).toLocaleString();
};

const getInvoice = async () => {
  try {
    loading.value = true;

    const response = await api.get(`admin/invoices/${route.params.id}`);

    if (response.data.success) {
      invoice.value = response.data.data;
    }
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  getInvoice();
});
</script>


<style scoped>
.page-title {
  font-weight: 700;
  color: #333;
}

.page-subtitle {
  color: #7b8190;
}

.back-btn {
  border-radius: 10px;
  padding: 10px 18px;
}

.info-card,
.table-card,
.summary-card {
  background: #fff;
  border-radius: 18px;
  padding: 22px;
  border: 1px solid #edf0f5;
  box-shadow: 0 8px 24px rgba(20, 30, 60, 0.05);
}

.card-title {
  font-weight: 700;
  color: #222;
}

.info-card p {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
  color: #7b8190;
}

.info-card p:last-child {
  margin-bottom: 0;
}

.info-card strong {
  color: #222;
  text-align: end;
}

.table {
  min-width: 820px;
}

.table thead th {
  color: #7b8190;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #edf0f5;
}

.table tbody td {
  padding: 16px 8px;
  color: #333;
}

.summary-card div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #6c7280;
}

.summary-card div:last-child {
  margin-bottom: 0;
}

.total-row {
  border-top: 1px solid #edf0f5;
  padding-top: 16px;
  margin-top: 16px;
  font-size: 18px;
  color: #222 !important;
}

.total-row strong {
  color: #4361ee;
}

@media (max-width: 576px) {
  .info-card p,
  .summary-card div {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
